<template>
  <div class="qbanks-screen" :class="{ 'qbanks-screen--rtl': isRtl }">
    <!-- Hero Header (matching MIA Dashboard) -->
    <div class="screen-hero">
      <div class="screen-hero__content">
        <button class="screen-hero__back" @click="goBack">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="screen-hero__icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <div class="screen-hero__text">
          <h1 class="screen-hero__title">{{ t("question_banks") }}</h1>
          <p class="screen-hero__subtitle">
            {{ t("question_banks_subtitle") }}
          </p>
        </div>
      </div>
      <div class="screen-hero__decoration">
        <div class="screen-hero__circle screen-hero__circle--1"></div>
        <div class="screen-hero__circle screen-hero__circle--2"></div>
      </div>
    </div>

    <!-- Active Uploads -->
    <ActiveUploadsWidget :feature-type="featureType" />

    <!-- Content -->
    <div class="screen-content">
      <!-- Create New Button (inline, not floating) -->
      <div class="create-section">
        <button class="create-btn" @click="navigateToCreate">
          <div class="create-btn__icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <div class="create-btn__text">
            <span class="create-btn__title">{{ t("create_new") }}</span>
            <span class="create-btn__desc">{{
              t("create_question_banks_desc")
            }}</span>
          </div>
          <svg
            class="create-btn__arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="screen-loading">
        <div class="spinner"></div>
        <span>{{ t("loading_question_banks") }}</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="screen-error">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>{{ error }}</p>
        <button class="btn btn--primary" @click="fetchQuestionBanks">
          {{ t("try_again") }}
        </button>
      </div>

      <!-- Empty -->
      <div v-else-if="questionBanks.length === 0" class="screen-empty">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <path
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
        <p>{{ t("no_question_banks_yet") }}</p>
        <span>{{ t("create_first_qbank") }}</span>
      </div>

      <!-- Question Banks List -->
      <div v-else>
        <h3 class="section-title">{{ t("your_question_banks") }}</h3>
        <div class="qbanks-list">
          <div
            v-for="bank in questionBanks"
            :key="bank.id"
            class="qbank-item"
            @click="navigateToBank(bank)"
          >
            <div class="qbank-item__icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
            <div class="qbank-item__content">
              <span class="qbank-item__title">{{
                bank.name || bank.title || "Untitled"
              }}</span>
              <span class="qbank-item__meta">
                {{ getTotalQuestions(bank) }} {{ t("questions") }}
                <template
                  v-if="
                    bank.status === 'completed' && bank.answered_questions_count
                  "
                >
                  <span class="qbank-item__separator">•</span>
                  {{ bank.answered_questions_count }}/{{
                    getTotalQuestions(bank)
                  }}
                  answered
                </template>
                <template
                  v-if="
                    bank.progress !== undefined && bank.status === 'completed'
                  "
                >
                  <span class="qbank-item__separator">•</span>
                  {{ bank.progress }}% progress
                </template>
              </span>
            </div>
            <div class="qbank-item__status-wrap">
              <span
                class="qbank-item__status"
                :class="getStatusClass(bank.status)"
              >
                {{ getStatusLabel(bank.status) }}
              </span>
              <span
                v-if="
                  bank.status === 'completed' && hasUnansweredQuestions(bank)
                "
                class="qbank-item__resume"
              >
                {{ t("resume") }}
              </span>
            </div>
            <button class="qbank-item__menu" @click.stop="openEditDialog(bank)">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </button>
          </div>

          <!-- Load More -->
          <button
            v-if="hasMore"
            class="load-more-btn"
            :disabled="loadingMore"
            @click="loadMore"
          >
            <span v-if="loadingMore" class="spinner spinner--small"></span>
            <span v-else>{{ t("load_more") }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Title Dialog -->
    <div
      v-if="showEditDialog"
      class="dialog-overlay"
      @click.self="closeEditDialog"
    >
      <div class="dialog">
        <h3 class="dialog__title">{{ t("edit_name") }}</h3>
        <input
          v-model="editTitle"
          type="text"
          class="dialog__input"
          :placeholder="t('enter_title')"
          maxlength="191"
        />
        <div class="dialog__actions">
          <button class="btn btn--secondary" @click="closeEditDialog">
            {{ t("cancel") }}
          </button>
          <button
            class="btn btn--primary"
            @click="saveTitle"
            :disabled="savingTitle"
          >
            {{ savingTitle ? t("loading") : t("save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { miaRefreshService } from "../services/refreshService";
import {
  MiaFeatureType,
  MiaRefreshType,
  StatusColors,
  OperationStatus,
} from "../types";
import { useI18n } from "../i18n";
import ActiveUploadsWidget from "../components/common/ActiveUploadsWidget.vue";

export default {
  name: "QuestionBanksScreen",
  components: {
    ActiveUploadsWidget,
  },
  setup() {
    const router = useRouter();
    const miaStore = useMiaStore();
    const { t, isRtl } = useI18n();

    const featureType = MiaFeatureType.QBANK;
    const page = ref(1);
    const loadingMore = ref(false);
    const showEditDialog = ref(false);
    const editingBank = ref(null);
    const editTitle = ref("");
    const savingTitle = ref(false);

    let unsubscribe = null;

    const questionBanks = computed(() => miaStore.state.questionBanks);
    const loading = computed(() => miaStore.state.questionBanksLoading);
    const error = computed(() => miaStore.state.questionBanksError);
    const hasMore = computed(() => {
      const meta = miaStore.state.questionBanksMeta;
      if (!meta) return false;
      return meta.current_page < Math.ceil(meta.total / meta.per_page);
    });

    const fetchQuestionBanks = async () => {
      page.value = 1;
      await miaStore.fetchQuestionBanks(1);
    };

    const loadMore = async () => {
      if (loadingMore.value) return;
      loadingMore.value = true;
      page.value++;
      await miaStore.fetchQuestionBanks(page.value);
      loadingMore.value = false;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const getStatusColor = (status) => {
      return StatusColors[status] || StatusColors.pending;
    };

    const goBack = () => {
      router.push("/mia");
    };

    const navigateToCreate = () => {
      router.push({
        path: "/mia/create",
        query: { feature: MiaFeatureType.QBANK },
      });
    };

    const navigateToBank = (bank) => {
      const status = (
        bank.status ||
        bank.generation_status ||
        ""
      ).toLowerCase();

      if (status === "completed") {
        // Navigate to MIA Quiz page (dedicated AI quiz experience)
        // MIA Quiz expects: qref (bankId), total (totalQuestionsCount), mode (quizMode), num (questionNumber)
        const totalQuestions =
          bank.total_questions_count ||
          bank.totalQuestionsCount ||
          bank.question_count ||
          0;
        const quizMode = bank.quiz_mode ?? bank.quizMode ?? 1;

        // Check if quiz is fully completed (all questions answered)
        const unanswered = bank.unanswered_questions || [];
        const isQuizCompleted = unanswered.length === 0 && totalQuestions > 0;

        // Determine starting question: first unanswered or 1
        let startQuestion = 1;
        if (unanswered.length > 0) {
          startQuestion = unanswered[0];
        }

        router.push({
          path: "/mia/quiz",
          query: {
            qref: bank.id,
            total: totalQuestions,
            mode: quizMode,
            num: startQuestion,
            // Pass completed flag to enable review mode
            ...(isQuizCompleted && { completed: "true" }),
          },
        });
      } else if (status === "processing" || status === "pending") {
        // Navigate to details screen to show progress
        router.push({
          path: "/mia/qbanks/details",
          query: {
            bankId: bank.id,
            bankTitle: bank.name || bank.title || "Question Bank",
          },
        });
      } else {
        // Failed or other status - show details
        router.push({
          path: "/mia/qbanks/details",
          query: {
            bankId: bank.id,
            bankTitle: bank.name || bank.title || "Question Bank",
          },
        });
      }
    };

    const openEditDialog = (bank) => {
      editingBank.value = bank;
      editTitle.value = bank.name || bank.title || "";
      showEditDialog.value = true;
    };

    const closeEditDialog = () => {
      showEditDialog.value = false;
      editingBank.value = null;
      editTitle.value = "";
    };

    const saveTitle = async () => {
      if (!editTitle.value.trim()) {
        alert("Title is required");
        return;
      }

      savingTitle.value = true;
      const result = await miaStore.updateQuestionBankTitle(
        editingBank.value.id,
        editTitle.value.trim()
      );
      savingTitle.value = false;

      if (result.success) {
        closeEditDialog();
        fetchQuestionBanks();
      } else {
        alert(result.error || "Failed to update title");
      }
    };

    const getTotalQuestions = (bank) => {
      return (
        bank.total_questions_count ||
        bank.totalQuestionsCount ||
        bank.question_count ||
        0
      );
    };

    const hasUnansweredQuestions = (bank) => {
      return bank.unanswered_questions && bank.unanswered_questions.length > 0;
    };

    const getStatusClass = (status) => {
      const s = (status || "").toLowerCase();
      return {
        "qbank-item__status--completed": s === "completed",
        "qbank-item__status--processing": s === "processing",
        "qbank-item__status--pending": s === "pending",
        "qbank-item__status--failed": s === "failed",
      };
    };

    const getStatusLabel = (status) => {
      const s = (status || "").toLowerCase();
      const labels = {
        completed: "Ready",
        processing: "Generating...",
        pending: "Pending",
        failed: "Failed",
      };
      return labels[s] || status;
    };

    onMounted(() => {
      fetchQuestionBanks();
      unsubscribe = miaRefreshService.subscribe(
        MiaRefreshType.QBANK,
        fetchQuestionBanks
      );
    });

    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
    });

    return {
      featureType,
      questionBanks,
      loading,
      error,
      hasMore,
      loadingMore,
      showEditDialog,
      editTitle,
      savingTitle,
      t,
      isRtl,
      fetchQuestionBanks,
      loadMore,
      formatDate,
      getStatusColor,
      getTotalQuestions,
      hasUnansweredQuestions,
      getStatusClass,
      getStatusLabel,
      goBack,
      navigateToCreate,
      navigateToBank,
      openEditDialog,
      closeEditDialog,
      saveTitle,
    };
  },
};
</script>

<style scoped>
/* ========== QUESTION BANKS SCREEN - UNIFIED DESIGN ========== */
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== Page Container ========== */
.qbanks-screen {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

/* ========== Hero Header (matching MIA Dashboard) ========== */
.screen-hero {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 32px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 30px 30px;
  box-shadow: 0 8px 32px rgba(0, 199, 68, 0.25);
}

.screen-hero__content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
}

.screen-hero__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.screen-hero__back:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(-2px);
}

.screen-hero__icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  color: white;
}

.screen-hero__text {
  flex: 1;
}

.screen-hero__title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.screen-hero__subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
}

.screen-hero__decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.screen-hero__circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.screen-hero__circle--1 {
  width: 150px;
  height: 150px;
  top: -40px;
  right: -30px;
  animation: pulse 4s ease-in-out infinite;
}

.screen-hero__circle--2 {
  width: 100px;
  height: 100px;
  bottom: -20px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

/* ========== Content Area ========== */
.screen-content {
  flex: 1;
  padding: 24px 20px 60px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

/* ========== Create New Button (Inline) ========== */
.create-section {
  margin-bottom: 24px;
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px 24px;
  background: white;
  border: 2px solid rgba(0, 196, 68, 0.15);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 196, 68, 0.08);
}

.create-btn:hover {
  border-color: #00c444;
  box-shadow: 0 8px 24px rgba(0, 196, 68, 0.15);
  transform: translateY(-2px);
}

.create-btn__icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.create-btn__text {
  flex: 1;
  text-align: left;
}

.create-btn__title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.create-btn__desc {
  display: block;
  font-size: 13px;
  color: #6b7280;
}

.create-btn__arrow {
  color: #00c444;
  transition: transform 0.3s ease;
}

.create-btn:hover .create-btn__arrow {
  transform: translateX(4px);
}

/* ========== Section Title ========== */
.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 16px 0;
}

/* ========== Loading, Error, Empty States ========== */
.screen-loading,
.screen-error,
.screen-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: #6b7280;
  min-height: 350px;
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.05);
}

.screen-empty svg,
.screen-error svg {
  margin-bottom: 20px;
  opacity: 0.6;
}

.screen-empty p,
.screen-error p {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.screen-empty span {
  font-size: 14px;
  color: #9ca3af;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 196, 68, 0.15);
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.spinner--small {
  width: 20px;
  height: 20px;
  border-width: 2px;
  margin: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== Question Banks List ========== */
.qbanks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qbank-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 196, 68, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.qbank-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 196, 68, 0.12);
  border-color: rgba(0, 196, 68, 0.2);
}

.qbank-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #d4f5e0 0%, #a8ebc4 100%);
  border-radius: 12px;
  color: #00c444;
}

.qbank-item__content {
  flex: 1;
  min-width: 0;
}

.qbank-item__title {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.qbank-item__meta {
  font-size: 13px;
  color: #6b7280;
}

.qbank-item__separator {
  margin: 0 6px;
}

.qbank-item__status-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.qbank-item__status {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: capitalize;
}

.qbank-item__status--completed {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.qbank-item__status--processing {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.qbank-item__status--pending {
  background: rgba(249, 115, 22, 0.1);
  color: #ea580c;
}

.qbank-item__status--failed {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.qbank-item__resume {
  font-size: 11px;
  font-weight: 600;
  color: #00c444;
}

.qbank-item__menu {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.2s ease;
}

.qbank-item__menu:hover {
  background: #e6ffee;
}

.load-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  margin-top: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.load-more-btn:hover:not(:disabled) {
  background: #e6ffee;
  border-color: #a8ebc4;
}

.load-more-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

/* ========== Dialog Overlay ========== */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.dialog {
  background: white;
  border-radius: 20px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 196, 68, 0.1);
}

.dialog__title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 20px 0;
}

.dialog__input {
  width: 100%;
  padding: 14px 18px;
  font-size: 15px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  background: #fafafa;
}

.dialog__input:focus {
  outline: none;
  border-color: #00c444;
  background: white;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.1);
}

.dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* ========== Buttons ========== */
.btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--primary {
  background: linear-gradient(135deg, #00c444 0%, #00a83a 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}

.btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 196, 68, 0.35);
}

.btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.btn--secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

/* RTL Support */
.qbanks-screen--rtl {
  direction: rtl;
  text-align: right;
}

.qbanks-screen--rtl .screen-hero__back svg {
  transform: scaleX(-1);
}

.qbanks-screen--rtl .create-btn__text {
  text-align: right;
}

.qbanks-screen--rtl .create-btn__arrow {
  transform: scaleX(-1);
}

.qbanks-screen--rtl .create-btn:hover .create-btn__arrow {
  transform: scaleX(-1) translateX(4px);
}

.qbanks-screen--rtl .qbank-item__status-wrap {
  align-items: flex-start;
}

.qbanks-screen--rtl .dialog__actions {
  justify-content: flex-start;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .qbanks-screen,
[data-theme="dark"] .qbanks-screen,
.dark .qbanks-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .screen-hero,
[data-theme="dark"] .screen-hero,
.dark .screen-hero {
  background: linear-gradient(135deg, #00a83b 0%, #008f32 50%, #007a2b 100%);
}

[data-bs-theme="dark"] .create-btn,
[data-theme="dark"] .create-btn,
.dark .create-btn {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.25);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .create-btn:hover,
[data-theme="dark"] .create-btn:hover,
.dark .create-btn:hover {
  background: #263548;
  border-color: #00c444;
}

[data-bs-theme="dark"] .create-btn__title,
[data-theme="dark"] .create-btn__title,
.dark .create-btn__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .create-btn__desc,
[data-theme="dark"] .create-btn__desc,
.dark .create-btn__desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-loading,
[data-bs-theme="dark"] .screen-error,
[data-bs-theme="dark"] .screen-empty,
[data-theme="dark"] .screen-loading,
[data-theme="dark"] .screen-error,
[data-theme="dark"] .screen-empty,
.dark .screen-loading,
.dark .screen-error,
.dark .screen-empty {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
  color: #94a3b8;
}

[data-bs-theme="dark"] .screen-empty p,
[data-bs-theme="dark"] .screen-error p,
[data-theme="dark"] .screen-empty p,
[data-theme="dark"] .screen-error p,
.dark .screen-empty p,
.dark .screen-error p {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-empty span,
[data-theme="dark"] .screen-empty span,
.dark .screen-empty span {
  color: #64748b;
}

[data-bs-theme="dark"] .qbank-item,
[data-theme="dark"] .qbank-item,
.dark .qbank-item {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .qbank-item:hover,
[data-theme="dark"] .qbank-item:hover,
.dark .qbank-item:hover {
  background: #263548;
}

[data-bs-theme="dark"] .qbank-item__title,
[data-theme="dark"] .qbank-item__title,
.dark .qbank-item__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .qbank-item__meta,
[data-theme="dark"] .qbank-item__meta,
.dark .qbank-item__meta {
  color: #64748b;
}

[data-bs-theme="dark"] .qbank-item__menu,
[data-theme="dark"] .qbank-item__menu,
.dark .qbank-item__menu {
  color: #64748b;
}

[data-bs-theme="dark"] .qbank-item__menu:hover,
[data-theme="dark"] .qbank-item__menu:hover,
.dark .qbank-item__menu:hover {
  background: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .load-more-btn,
[data-theme="dark"] .load-more-btn,
.dark .load-more-btn {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.25);
  color: #00c444;
}

[data-bs-theme="dark"] .load-more-btn:hover:not(:disabled),
[data-theme="dark"] .load-more-btn:hover:not(:disabled),
.dark .load-more-btn:hover:not(:disabled) {
  background: #263548;
}

[data-bs-theme="dark"] .dialog,
[data-theme="dark"] .dialog,
.dark .dialog {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .dialog__title,
[data-theme="dark"] .dialog__title,
.dark .dialog__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .dialog__input,
[data-theme="dark"] .dialog__input,
.dark .dialog__input {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .dialog__input:focus,
[data-theme="dark"] .dialog__input:focus,
.dark .dialog__input:focus {
  background: #1e293b;
  border-color: #00c444;
}

[data-bs-theme="dark"] .btn--secondary,
[data-theme="dark"] .btn--secondary,
.dark .btn--secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn--secondary:hover,
[data-theme="dark"] .btn--secondary:hover,
.dark .btn--secondary:hover {
  background: #475569;
  border-color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .screen-hero {
    padding: 24px 16px;
    border-radius: 0 0 24px 24px;
  }

  .screen-hero__title {
    font-size: 1.25rem;
  }

  .screen-hero__icon {
    width: 48px;
    height: 48px;
  }

  .screen-content {
    padding: 20px 16px 40px;
  }

  .create-btn {
    padding: 16px 20px;
  }

  .create-btn__icon {
    width: 44px;
    height: 44px;
  }

  .qbank-item {
    padding: 14px 16px;
  }

  .dialog {
    padding: 24px;
    margin: 16px;
  }
}

@media (max-width: 480px) {
  .screen-hero__icon {
    display: none;
  }
}
</style>
